import React, { useEffect, useState } from 'react';

import { Box, Flex, Grid, NumberInput, Select, Text } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { Flow } from 'types';

import { formatDeliveryModeAfterText } from '../../Flows/components/FlowDeliveryModeFormatter';
import { TriggerTypes } from '../types';

interface IFlowBuilderDeliveryTypeContainer {
  selectedTriggerType: TriggerTypes;
  selectedDeliveryMode: Flow['deliveryMode'];
  selectedTimeUnit: Flow['timeUnit'];
  selectedTimeAmount: number | null;
  selectedTrigger: Flow['trigger'] | null;
  sendAtTime: string | null;
  isHourUnitAllowed: boolean;
  handleDeliveryModeChange: (value: Flow['deliveryMode']) => void;
  handleTimeUnitChange: (value: Flow['timeUnit']) => void;
  handleTimeAmountChange: (value: number) => void;
  handleSendAtTimeChange: (value: string | null) => void;
}

const FlowBuilderDeliveryTypeContainer = ({
  selectedTriggerType,
  selectedDeliveryMode,
  selectedTimeUnit,
  selectedTimeAmount,
  selectedTrigger,
  sendAtTime,
  isHourUnitAllowed,
  handleDeliveryModeChange,
  handleTimeUnitChange,
  handleTimeAmountChange,
  handleSendAtTimeChange,
}: IFlowBuilderDeliveryTypeContainer): JSX.Element => {
  const immediateAndDelayed: string[] = ['Immediate', 'Delayed'];
  const reminder: string[] = ['Reminder'];
  const immediate: string[] = ['Immediate'];

  const [isCheckInSpecialTrigger, setIsCheckInSpecialTrigger] = useState<boolean>(false);
  useEffect(() => {
    if (selectedTrigger === 'Check-ins completed') {
      setIsCheckInSpecialTrigger(true);
    } else {
      setIsCheckInSpecialTrigger(false);
    }
  }, [selectedTrigger]);

  const handleSelectTimeUnit = (value: Flow['timeUnit']): void => {
    if (value == 'Days') {
      if (selectedTimeAmount && selectedTimeAmount > 30) {
        handleTimeAmountChange(30);
      }
      if (!isHourUnitAllowed && sendAtTime === null) {
        handleSendAtTimeChange('07:00');
      }
    } else if (value == 'Hours') {
      if (selectedTimeAmount && selectedTimeAmount > 23) {
        handleTimeAmountChange(23);
      }
      handleSendAtTimeChange(null);
    }
    handleTimeUnitChange(value);
  };

  return (
    <Box w='80%'>
      <Text size='md' weight={500} mb='sm'>
        Send a notification at a pre-determined time
      </Text>
      <Grid columns={10}>
        <Grid.Col span={10}>
          <Select
            label='Select a delivery type'
            data={
              isCheckInSpecialTrigger
                ? immediate
                : selectedTriggerType === 'immediateAndDelayed'
                ? immediateAndDelayed
                : reminder
            }
            value={selectedDeliveryMode}
            onChange={value => handleDeliveryModeChange(value as Flow['deliveryMode'])}
            allowDeselect={false}
            placeholder='Choose delivery mode'
            mb='md'
            disabled={isCheckInSpecialTrigger || selectedTriggerType === 'reminder'}
            // style={{ maxWidth: '300px' }}
          />
        </Grid.Col>
      </Grid>

      {isCheckInSpecialTrigger && (
        <>
          <Grid columns={8}>
            <Grid.Col span={1}>
              <Flex align='center' justify='flex-start' style={{ height: '100%' }}>
                <Text>After</Text>
              </Flex>
            </Grid.Col>
            <Grid.Col span={2}>
              <NumberInput
                min={1}
                max={1000}
                value={selectedTimeAmount || undefined}
                onChange={value => handleTimeAmountChange(value as number)}
                hideControls
                placeholder={'Number of Check-ins'}
              />
            </Grid.Col>
            <Grid.Col span={5}>
              <Flex align='center' justify='flex-start' style={{ height: '100%' }}>
                <Text>
                  {formatDeliveryModeAfterText({
                    trigger: selectedTrigger,
                    deliveryMode: selectedDeliveryMode,
                  })}
                </Text>
              </Flex>
            </Grid.Col>
          </Grid>
        </>
      )}

      {selectedDeliveryMode !== 'Immediate' && (
        <>
          <Grid columns={10}>
            <Grid.Col span={3}>
              <NumberInput
                min={selectedTrigger === 'Birthday upcoming' ? 0 : 1}
                max={selectedTimeUnit === 'Days' ? 30 : 23}
                value={selectedTimeAmount || undefined}
                onChange={value => handleTimeAmountChange(value as number)}
                hideControls
                placeholder={selectedDeliveryMode === 'Delayed' ? 'Delay time' : 'Reminder time'}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                data={isHourUnitAllowed ? ['Hours', 'Days'] : ['Days']}
                value={selectedTimeUnit}
                onChange={value => handleSelectTimeUnit(value as Flow['timeUnit'])}
                allowDeselect={false}
                placeholder='Select time unit'
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Flex align='center' justify='flex-start' style={{ height: '100%' }}>
                <Text>
                  {formatDeliveryModeAfterText({
                    trigger: selectedTrigger,
                    deliveryMode: selectedDeliveryMode,
                  })}
                </Text>
              </Flex>
            </Grid.Col>
          </Grid>
          {selectedTimeUnit === 'Days' && !isHourUnitAllowed && (
            <Grid columns={10}>
              <Grid.Col span={10}>
                <TimeInput
                  label='At'
                  value={sendAtTime || '07:00'}
                  onChange={event => handleSendAtTimeChange(event.target.value)}
                  required
                />
              </Grid.Col>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default FlowBuilderDeliveryTypeContainer;
